import { computed, inject, Signal } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  pipe,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  CountryCode,
  Frequency,
  LanguageOption,
  RelationGroup,
  Role,
  Timezone,
  Unit,
} from '@qtek/shared/models';
import { extractKeys } from '@qtek/shared/utils';
import { MetaService } from './meta.service';
import { PowerSearchService } from './power-search.service';

/** Scope of a power-search (saved view) record — backend MetaTypeEnum. */
export type MetaType = 'prs' | 'org' | 'all';

/** A single `mta` record as serialized by the backend (Mtaapi view). */
export interface PowerSearchMetaApi {
  mid?: string;
  tp?: MetaType;
  /** JSON-encoded MetaControlsData; omitted (NON_EMPTY) when there is no saved state. */
  data?: string;
  _id?: string;
  _cts?: string;
  _uts?: string;
}

/** powerSearchMeta[mid]: the personal/org pair built from the subscribe result. */
export interface PowerSearchMetaEntry {
  mid: string;
  prs: PowerSearchMetaApi;
  org: PowerSearchMetaApi;
}

interface MetaState {
  roles: {
    user: Role[];
    vendor: Role[];
    customer: Role[];
    staff: Role[];
  };
  units: Unit[];
  frequencies: Frequency[];
  languages: LanguageOption[];
  mobileTitle: string;
  title: string;
  countryCode: CountryCode[] | null;
  guiShrDomain: string;
  guiDomain: string;
  timezones: Timezone[] | null;
  guiOnlineBookDomain: string;
  wsPingPong: number | null;
  curs: string[] | null;
  licenseCmpId: string;
  countries: Array<{ id: string; nm: string }>;
  countryInfo: any;
  states: { [key: string]: string[] } | null;
  licenseWebId: string;
  token: string | null;
  darkTheme: boolean;
  powerSearchMeta: Record<string, PowerSearchMetaEntry>;
  wsVersion: string | null;
}

const initialState: MetaState = {
  roles: { user: [], vendor: [], customer: [], staff: [] },
  units: [],
  frequencies: [],
  languages: [],
  mobileTitle: '',
  title: '',
  countryCode: null,
  guiShrDomain: '',
  guiDomain: '',
  timezones: null,
  curs: null,
  guiOnlineBookDomain: '',
  wsPingPong: null,
  licenseCmpId: '',
  licenseWebId: '',
  countries: [],
  countryInfo: null,
  states: null,
  token: null,
  darkTheme: false,
  powerSearchMeta: {},
  wsVersion: null,
};

function applyMetaResponse(res: any): Partial<MetaState> {
  const patch: Partial<MetaState> = {};
  if (res.unts) patch.units = extractKeys(res.unts) as Unit[];
  if (res.frq) patch.frequencies = extractKeys(res.frq) as Frequency[];
  if (res.ptlRoleGroups) {
    patch.roles = [
      { id: RelationGroup.USER, name: 'user' },
      { id: RelationGroup.VENDOR, name: 'vendor' },
      { id: RelationGroup.CUSTOMER, name: 'customer' },
      { id: RelationGroup.STAFF, name: 'staff' },
    ].reduce((o: any, role) => {
      o[role.name] = Object.keys(res.ptlRoleGroups[role.id].roles || {}).map(
        (id: string) => ({ id, name: res.ptlRoleGroups[role.id].roles[id] })
      );
      return o;
    }, {});
  }
  if (res.lngs) patch.languages = extractKeys(res.lngs) as LanguageOption[];
  if (res.ptl) {
    patch.mobileTitle = res.ptl.title1;
    patch.title = res.ptl.title2;
    patch.guiDomain = res.ptl.guiDomain;
    patch.guiOnlineBookDomain = res.ptl.guiOnlineBookDomain;
    patch.guiShrDomain = res.ptl.guiShrDomain;
  }
  if (res.phns?.cntrs) patch.countryCode = res.phns.cntrs;
  if (res.tzs) {
    patch.timezones = Object.keys(res.tzs).map(key => ({
      timezone: key,
      names: res.tzs[key],
    }));
  }
  if (res.wsPingPong) patch.wsPingPong = res.wsPingPong;
  if (res.curs) patch.curs = res.curs;
  if (res.licCmp) patch.licenseCmpId = res.licCmp;
  if (res.licWebId) patch.licenseWebId = res.licWebId;
  if (res.adr) patch.states = res.adr.stateForCountry;
  return patch;
}

function buildPsPayloadByMid(
  res: PowerSearchMetaApi[],
  mid: string
): PowerSearchMetaEntry {
  return res?.[0]
    ? { prs: { ...res[0], mid }, org: { ...res[1], mid }, mid }
    : { prs: { mid, tp: 'prs' }, org: { mid, tp: 'org' }, mid };
}

export const MetaStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods(
    (
      store,
      service = inject(MetaService),
      psService = inject(PowerSearchService)
    ) => {
      const disconnect$ = new Subject<string>();
      const psUpdate$ = new Subject<{
        mid: string;
        data: PowerSearchMetaEntry;
      }>();
      const psMetaSignalCache = new Map<
        string,
        Signal<PowerSearchMetaEntry | undefined>
      >();

      const pendingKeys = new Set<string>();

      const loadMetaData = rxMethod<string[]>(
        pipe(
          mergeMap(params =>
            service.getMetaData(params).pipe(
              tap(({ res }) => {
                params.forEach(key => pendingKeys.delete(key));
                patchState(store, applyMetaResponse(res));
              }),
              catchError(() => {
                params.forEach(key => pendingKeys.delete(key));
                return of(null);
              })
            )
          )
        )
      );

      const lazy = (ready: () => boolean, keys: string[]): void => {
        if (ready() || keys.some(key => pendingKeys.has(key))) return;
        keys.forEach(key => pendingKeys.add(key));
        loadMetaData(keys);
      };

      return {
        // ── UI theme ────────────────────────────────────────────────────────────
        setDarkTheme(isDark: boolean): void {
          localStorage.setItem('uiTheme', isDark ? 'dark' : 'light');
          patchState(store, { darkTheme: isDark });
        },

        // ── Lazy HTTP meta ──────────────────────────────────────────────────────
        loadGuiDomain: () => lazy(() => !!store.guiDomain(), ['ptl']),
        loadTitle: () => lazy(() => !!store.title(), ['ptl']),
        loadMobileTitle: () => lazy(() => !!store.mobileTitle(), ['ptl']),
        loadGuiShrDomain: () => lazy(() => !!store.guiShrDomain(), ['ptl']),
        loadGuiOnlineBookDomain: () =>
          lazy(() => !!store.guiOnlineBookDomain(), ['ptl']),
        loadTimezones: () => lazy(() => !!store.timezones()?.length, ['tzs']),
        loadUnits: () => lazy(() => !!store.units().length, ['unts']),
        loadFrequencies: () =>
          lazy(() => !!store.frequencies().length, ['frq']),
        loadRoles: () =>
          lazy(() => !!store.roles().user.length, ['ptlRoleGroups']),
        loadLanguages: () => lazy(() => !!store.languages().length, ['lngs']),
        loadCountryCode: () => lazy(() => !!store.countryCode(), ['phns']),
        loadWebsocket: () => lazy(() => !!store.wsPingPong(), ['wsPingPong']),
        loadCurrency: () => lazy(() => !!store.curs(), ['curs']),
        loadCompanyLicense: () =>
          lazy(() => !!store.licenseCmpId(), ['licCmp']),
        loadStates: () => lazy(() => !!store.states(), ['adr']),

        // ── Countries HTTP ──────────────────────────────────────────────────────
        loadCountries: rxMethod<void>(
          pipe(
            filter(() => !store.countries().length),
            switchMap(() =>
              service.getMetaDataCountries().pipe(
                tap(({ res }) => patchState(store, { countries: res || [] })),
                catchError(() => of(null))
              )
            )
          )
        ),

        loadCountryInfo: rxMethod<string>(
          pipe(
            switchMap(id =>
              service.getMetaDataCountryInfo(id).pipe(
                tap(({ res }) => patchState(store, { countryInfo: res })),
                catchError(() => of(null))
              )
            )
          )
        ),

        // ── Misc setters ────────────────────────────────────────────────────────
        setToken(token: string): void {
          patchState(store, { token });
        },
        setWsVersion(version: string): void {
          patchState(store, { wsVersion: version });
        },

        // ── Power search WS (multi-channel, Pattern C) ──────────────────────────
        connectPowerSearch: rxMethod<string>(
          pipe(
            mergeMap(mid =>
              psService.subscribe({ prms: { mid }, s: 'model' }).pipe(
                takeUntil(disconnect$.pipe(filter(id => id === mid))),
                tap(({ res, op }: any) => {
                  switch (op) {
                    case 'query': {
                      const payload = buildPsPayloadByMid(res, mid);
                      patchState(store, state => ({
                        powerSearchMeta: {
                          ...state.powerSearchMeta,
                          [mid]: payload,
                        },
                      }));
                      psUpdate$.next({ mid, data: payload });
                      break;
                    }
                    case 'upd':
                      if (res) {
                        patchState(store, state => ({
                          powerSearchMeta: {
                            ...state.powerSearchMeta,
                            [mid]: {
                              ...state.powerSearchMeta[mid],
                              [res.tp]: res,
                            },
                          },
                        }));
                      }
                      break;
                  }
                })
              )
            )
          )
        ),

        disconnectPowerSearch(mid: string): void {
          disconnect$.next(mid);
          psMetaSignalCache.delete(mid);
          patchState(store, state => {
            const { [mid]: _, ...rest } = state.powerSearchMeta;
            return { powerSearchMeta: rest };
          });
        },

        updatePowerSearchMeta(payload: {
          mid: string;
          data: string;
          tp?: 'prs' | 'org';
        }): void {
          psService.updateMeta({
            res: { ...payload, ...(payload.tp && { tp: payload.tp }) },
          });
        },

        getPSMetaByMid(mid: string) {
          let cached = psMetaSignalCache.get(mid);
          if (!cached) {
            cached = computed(() => store.powerSearchMeta()[mid]);
            psMetaSignalCache.set(mid, cached);
          }
          return cached;
        },

        watchPSMeta(mid: string): Observable<PowerSearchMetaEntry> {
          return psUpdate$.pipe(
            filter(u => u.mid === mid),
            map(u => u.data)
          );
        },
      };
    }
  ),

  withComputed(({ roles }) => ({
    allRoles: computed(() => [
      ...roles().user,
      ...roles().vendor,
      ...roles().staff,
      ...roles().customer,
    ]),
  })),

  withHooks(store => ({
    onInit() {
      const theme = localStorage.getItem('uiTheme');
      patchState(store, { darkTheme: theme === 'dark' });
    },
  }))
);
